import React from 'react';
import { ArrowRight, Users, TrendingUp, Gift, Shield, Zap, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
// import components
import Header from '../components/Header';
import Footer from '../components/Footer';

const Homepage = () => {

    // hooks
    const { isAuth } = useSelector((state) => state.auth);

    const features = [
        {
            icon: Users,
            title: 'Binary Tree System',
            description: 'Build your network with our proven 1-2 or 2-1 binary plan structure'
        },
        {
            icon: TrendingUp,
            title: 'Progressive Income',
            description: 'Each level doubles your earnings as you grow your network'
        },
        {
            icon: Gift,
            title: 'Cash Awards',
            description: 'Earn up to ₹2,00,000 in cash awards and business development funds'
        },
        {
            icon: Shield,
            title: 'Secure Platform',
            description: 'Your data and transactions are protected with bank-level security'
        },
        {
            icon: Zap,
            title: 'Instant Activation',
            description: 'Get started immediately with our welcome kit and training'
        },
        {
            icon: Target,
            title: 'Director Bonus',
            description: '2% of company income shared among Level 13 achievers'
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
            {/* Hero Section */}
            <div className="relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-20 right-20 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl"></div>
                    <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
                </div>

                {/* Header */}
                <Header />

                {/* Hero Content */}
                <div className="relative z-10 px-6 py-20 md:py-32">
                    <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                                Gateway Promoters<br />
                                <span className="text-cyan-400">Binary Plan</span>
                            </h2>
                            <p className="text-xl text-gray-300 mb-8">
                                Unlocking Your Path to Revenue Growth
                            </p>
                            <p className="text-gray-400 mb-8 text-lg">
                                Join our revolutionary binary plan system and build a sustainable income stream.
                                Start with just ₹2000 and watch your network grow exponentially.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                {
                                    isAuth ? (
                                        <Link to="/dashboard" className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-semibold hover:shadow-xl hover:shadow-cyan-500/50 transition-all flex items-center gap-2 group">
                                            Go to Dashboard
                                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                        </Link>

                                    ) : (
                                        <Link to="/register" className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-semibold hover:shadow-xl hover:shadow-cyan-500/50 transition-all flex items-center gap-2 group">
                                            Join Now
                                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                        </Link>
                                    )
                                }

                                <Link to="/about-us" className="px-8 py-4 border-2 border-cyan-500 text-cyan-400 rounded-lg font-semibold hover:bg-cyan-500 hover:text-white transition-all">
                                    About Us
                                </Link>
                            </div>
                        </div>

                        <div className="relative bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-cyan-500/20">
                            <img
                                src="/images/binary-plan-hero.jpg"
                                alt="Gateway Promoters Binary Plan"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Level-wise Earnings Section */}
            <div className="relative px-6 py-20 bg-slate-800/50">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-12 flex justify-center">
                        <img
                            src="/images/level-wise-earnings.jpg"
                            alt="Level-wise Earnings Chart"
                            className="rounded-2xl shadow-2xl max-w-4xl w-full border border-cyan-500/20"
                        />
                    </div>

                    {/* <div className="grid md:grid gap-12 items-center"> */}
                    <div>
                        <div className="bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-2xl p-8 border border-cyan-500/30 mb-6">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <Gift className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-cyan-400 mb-2">₹2000 Refund</h3>
                                    <p className="text-gray-300">Joining fee reimbursed at Level 5</p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center gap-3 text-gray-300">
                                <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                                <span>Level 1: Start earning ₹500</span>
                            </div>
                            <div className="flex items-center gap-3 text-gray-300">
                                <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                                <span>Level 2: Income doubles to ₹1000</span>
                            </div>
                            <div className="flex items-center gap-3 text-gray-300">
                                <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                                <span>Level 3: Earn ₹2000 per cycle</span>
                            </div>
                            <div className="flex items-center gap-3 text-gray-300">
                                <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                                <span>Level 4: Income reaches ₹4000</span>
                            </div>
                            <div className="flex items-center gap-3 text-cyan-400 font-semibold">
                                <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                                <span>Level 5: Earn ₹8000 + Get ₹2000 refund!</span>
                            </div>
                        </div>
                    </div>
                    {/* </div> */}
                </div>
            </div>

            {/* Features Section */}
            <div className="px-6 py-20">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            Why Choose <span className="text-cyan-400">Gateway Promoters?</span>
                        </h2>
                        <p className="text-xl text-gray-400">Everything you need to succeed in network marketing</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {features.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <div
                                    key={index}
                                    className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-cyan-500/20 hover:border-cyan-500/50 transition-all hover:transform hover:scale-105 cursor-pointer"
                                >
                                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                                        <Icon className="w-7 h-7 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                                    <p className="text-gray-400">{feature.description}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="px-6 py-20 bg-gradient-to-r from-cyan-500 to-blue-600">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Ready to Start Your Journey?
                    </h2>
                    <p className="text-xl text-white/90 mb-8">
                        Join thousands of successful promoters and build your path to financial freedom
                    </p>
                    {/* <div className="flex flex-wrap justify-center gap-4">
                        <button className="px-8 py-4 bg-white text-cyan-600 rounded-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl">
                            Register Now
                        </button>
                        <button className="px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-cyan-600 transition-all">
                            Contact Us
                        </button>
                    </div> */}
                    <div className="flex flex-wrap justify-center gap-4">
                        {
                            isAuth ? (
                                <Link to="/dashboard" className="px-8 py-4 bg-white text-cyan-600 rounded-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl">
                                    Get Started
                                </Link>
                            ) : (
                                <Link to="/register" className="px-8 py-4 bg-white text-cyan-600 rounded-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl">
                                    Register Now
                                </Link>
                            )
                        }

                        <Link to="/contact-us" className="px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-cyan-600 transition-all">
                            Contact Us
                        </Link>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <Footer />
        </div >
    );
};

export default Homepage;